'use client';
import React, { useState, useEffect } from 'react';

interface LocationItem {
  id: number;
  name: string;
  slug: string;
  banner_image: string | null;
  type: string;
}

export default function AreasSection() {
  const [locationsMap, setLocationsMap] = useState<Record<string, LocationItem>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchLocations() {
      try {
        const res = await fetch('/api/taxonomies/locations');
        if (res.ok) {
          const data = await res.json();
          const items = Array.isArray(data) ? data : [];

          // Map by slug for dynamic lookup
          const map: Record<string, LocationItem> = {};
          items.forEach((item: LocationItem) => {
            map[item.slug] = item;
          });
          setLocationsMap(map);
        }
      } catch (err) {
        console.error('Failed to fetch dynamic locations:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchLocations();
  }, []);

  // Helper to render card with dynamic image/grey state
  const renderCard = (
    slug: string,
    heightClass: string,
    displayName: string,
    forceGrey: boolean = false
  ) => {
    const item = locationsMap[slug];
    const name = displayName;
    const bannerImage = forceGrey
      ? null
      : item?.banner_image || (slug === 'business-bay' ? '/uploads/dubai-cityscape.jpg' : null);
    const url = item ? `/${item.slug}` : `/${slug}`;

    return (
      <a
        href={url}
        className={`relative ${heightClass} block overflow-hidden group shadow-lg hover:-translate-y-2 transition-all duration-300 rounded-none cursor-pointer`}
        style={{
          backgroundColor: bannerImage ? 'transparent' : '#b5b7b5',
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.08), 0 8px 10px -6px rgba(0, 0, 0, 0.08)',
        }}
      >
        {bannerImage && (
          <>
            <img
              src={bannerImage}
              alt={name}
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.05) 50%, rgba(0,0,0,0.5) 100%)',
              }}
            />
          </>
        )}
        <div className="absolute top-0 left-0 right-0 p-6 z-10">
          <h3
            className="text-white uppercase"
            style={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '16px',
              fontWeight: 600,
              lineHeight: '22px',
              letterSpacing: '0.05em',
            }}
          >
            {name}
          </h3>
        </div>
      </a>
    );
  };

  return (
    <section className="bg-white py-16">
      <div className="max-w-[1280px] mx-auto px-4 lg:px-6">
        {/* TOP ROW: Title + Business Bay (left), Big Downtown (center), 2 Small Stacked (right) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {/* COLUMN 1: Left */}
          <div className="flex flex-col justify-between" style={{ minHeight: '480px' }}>
            {/* Title Block */}
            <div className="mb-4">
              <h2
                className="mb-4"
                style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '30px',
                  fontWeight: 300,
                  color: 'rgb(80, 81, 79)',
                  lineHeight: '38px',
                }}
              >
                Premium Locations In Dubai
              </h2>
              <p
                className="mb-6 max-w-[280px]"
                style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '14px',
                  fontWeight: 400,
                  color: 'rgb(120, 120, 120)',
                  lineHeight: '22px',
                }}
              >
                Dubai&apos;s finest addresses, now within reach. Explore premium plots for sale in
                Dubai&apos;s elite locations.
              </p>

              {/* Explore All Areas Button */}
              <div className="inline-block">
                <a
                  href="https://plotae.com/all-areas/"
                  className="inline-block bg-[#5a6872] text-white px-5 py-2 text-[13px] font-semibold tracking-wide hover:bg-[#4a5862] transition-colors duration-200 rounded-none uppercase"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  Explore All Areas
                </a>
                {/* Custom Green Underline Bar */}
                <div className="h-[2px] bg-[#017743] mt-4 w-28" />
              </div>
            </div>

            {/* Business Bay Card (Image) */}
            {renderCard('business-bay', 'h-[250px]', 'Business Bay', false)}
          </div>

          {/* COLUMN 2: Center (Big Downtown Card) */}
          <div className="h-full">
            {renderCard('downtown', 'h-full min-h-[480px]', 'Down Town', false)}
          </div>

          {/* COLUMN 3: Right (2 Small Stacked Cards) */}
          <div className="flex flex-col gap-6" style={{ minHeight: '480px' }}>
            {renderCard('jvc', 'flex-1 h-[228px]', 'Jumeirah Village Circle', false)}
            {renderCard('jumeirah-garden-city', 'flex-1 h-[228px]', 'Jumeirah Garden City', false)}
          </div>
        </div>

        {/* BOTTOM ROW: 3 equal horizontal boxes */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {renderCard('dubai-silicon-oasis', 'h-[280px]', 'Silicon Oasis', false)}
          {renderCard('dubai-island', 'h-[280px]', 'Dubai Island', false)}
          {renderCard('dlrc', 'h-[280px]', 'Dubai Land Residence Complex', false)}
        </div>
      </div>
    </section>
  );
}
